import { useRef, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Settings } from "lucide-react";

import { usePlanner } from "../contexts/PlannerContext";
import { PlannerSettings } from "./Settings";

export const ViewTypeSelector = () => {
    const { settings, updateSettings, portal } = usePlanner();

    const viewTypeToValue = (viewType: string): string => {
        return viewType || "week";
    };

    const setCalendarViewType = async (viewType: string) => {
        updateSettings({ assignments_calendar_view_type: viewType });
    };

    const currentView = settings?.assignments_calendar_view_type || "week";

    return (
        <Select
            value={viewTypeToValue(currentView)}
            onValueChange={(value) => setCalendarViewType(value)}
        >
            <SelectTrigger size="sm" variant="theme" className="w-fit px-[10px] h-[30px] text-xs">
                <SelectValue />
            </SelectTrigger>
            <SelectContent variant="theme" container={portal}>
                <SelectItem variant="theme" value="day">Day</SelectItem>
                <SelectItem variant="theme" value="5day">5 Days</SelectItem>
                <SelectItem variant="theme" value="week">Week</SelectItem>
                <SelectItem variant="theme" value="month">Month</SelectItem>
            </SelectContent>
        </Select>
    );
};

type PlanMyButtonProps = {
    label?: string;
    className?: string;
};

export const PlanMyButton = ({ label, className }: PlanMyButtonProps) => {
    const { settings } = usePlanner();
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const lastToggleTime = useRef(0);

    const calendarViewType = settings?.assignments_calendar_view_type || "week";

    const getPlanText = () => {
        if (calendarViewType === "day") return "Plan My Day";
        if (calendarViewType === "month") return "Plan My Month";
        if (calendarViewType === "5day") return "Plan My 5 Days";
        return "Plan My Week";
    };

    const handleSettingsOpenChange = (newOpen: boolean) => {
        const now = Date.now();
        const timeSinceLastToggle = now - lastToggleTime.current;

        if (timeSinceLastToggle < 100) {
            return;
        }

        lastToggleTime.current = now;
        setSettingsOpen(newOpen);
    };

    const buttonLabel = label || getPlanText();

    return (
        <Popover open={settingsOpen} onOpenChange={handleSettingsOpenChange}>
            <PopoverTrigger asChild>
                <button className={`flex items-center gap-1.5 cursor-pointer hover:bg-theme-1 rounded-md px-2 py-1 transition-colors ${className || ""}`}>
                    <p className="text-sm font-semibold">{buttonLabel}</p>
                    <Settings className="size-4 text-theme-1" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                className={`p-1 transition-all duration-200 ${dialogOpen ? "opacity-0 pointer-events-none" : ""}`}
                style={dialogOpen ? { visibility: "hidden" } : undefined}
            >
                <PlannerSettings
                    onDialogOpen={() => setDialogOpen(true)}
                    onDialogClose={() => setDialogOpen(false)}
                />
            </PopoverContent>
        </Popover>
    );
};
